import {inject, Injectable} from '@angular/core';
import {BaseService} from "../BaseService";
import {MqttManagerService} from "@aiknow-srl/aik-ngx-mqtt";
import {environment} from "../../../environments/environment";
import {filter, Subject, Subscription} from "rxjs";
import {EventBusService, EventData, IBusService} from "@aiknow-srl/aik-ngx-eventbus";
import {NotificationTokenExpiredNtf} from "../../events/notification/NotificationTokenExpiredNtf";
import {MetricTelemetryNotificationService} from "./interfaces/MetricTelemetryNotificationService";
import {NotificationSubscription} from "../../models/notification/NotificationSubscription";
import {UpdateMetricValueNtf} from "../../events/notification/UpdateMetricValueNtf";
import {MetricValueUpdate} from "../../models/notification/MetricValue";
import {BaseEvent} from "../../events/BaseEvent";
import {UserOpeService} from "../ope/userOpe.service";
import {RefreshPushNotificationTokenCB} from "../../ope/user/RefreshPushNotificationTokenCB";
import {GlobalModelService} from "../GlobalModel.service";
import {GatewayAlarmNotificationService} from "./interfaces/GatewayAlarmNotificationService";
import {GatewayStatusChangedNtf} from "../../events/notification/GatewayStatusChangedNtf";
import {DeviceAlarmNotificationService} from "./interfaces/DeviceAlarmNotificationService";
import {DeviceStatusChangedNtf} from "../../events/notification/DeviceStatusChangedNtf";
import {GatewayAlarmStatus} from "../../models/notification/GatewayAlarmStatus";
import {DeviceAlarmStatus} from "../../models/notification/DeviceAlarmStatus";
import {MetricAlarmStatusChangedLiteNtf} from "../../events/notification/MetricAlarmStatusChangedLiteNtf";
import {MetricAlarmStatusDetail, MetricAlarmStatusLite} from "../../models/notification/MetricAlarmStatusLite";
import {MetricAlarmNotificationService} from "./interfaces/MetricAlarmNotificationService";
import {EventBusNotificationService} from "./interfaces/EventBusService";
import {MetricAlarmStatusChangedDetailNtf} from "../../events/notification/MetricAlarmStatusChangedDetailNtf";
import {
  ApplicationOnlineStatusChangedBody,
  ApplicationOnlineStatusChangedNtf
} from "../../events/notification/ApplicationOnlineStatusChangedNtf";
import {GatewayWorkflowNotificationService} from "./interfaces/GatewayWorkflowNotificationService";
import {GatewayWorkflowChangedNtf} from 'src/app/events/notification/GatewayWorkflowChangedNtf';
import {GatewayWorkflowStatus} from "../../models/notification/GatewayWorkflowStatus";
import {WriteMetricNotificationService} from "./interfaces/WriteMetricNotificationService";
import {WriteMetricValueNtf} from "../../events/notification/WriteMetricValueNtf";
import {WriteMetricValue} from "../../models/notification/WriteMetricValue";
import {WriteOnEdgeStatusEnum} from "../../enums/dashboard/WriteOnEdgeStatusEnum";

@Injectable({
  providedIn: 'root'
})
export class NotificationService extends BaseService implements MetricTelemetryNotificationService,
  GatewayAlarmNotificationService,
  GatewayWorkflowNotificationService,
  DeviceAlarmNotificationService,
  MetricAlarmNotificationService,
  EventBusNotificationService,
  WriteMetricNotificationService {

  /**
   * Service used for api calls related to users
   * @protected
   */
  protected userService = inject(UserOpeService);
  private mqttManager: MqttManagerService = inject(MqttManagerService);
  private externalBusService: IBusService = inject(EventBusService);
  private gmService: GlobalModelService = inject(GlobalModelService);
  // Connection FIELDS
  private pushNotificationUserToken?: string;
  private blockForExpiredToken: boolean = false;

  // GLOBAL NTF OBJECTS
  private externalEventSubscriptionList$!: string[];
  private externalEventSubscriptions$!: NotificationSubscription[];
  private mapExtIntEvents!: Map<string, string>;
  private mapEventTs!: Map<string, number>;
  private subject$: Subject<BaseEvent> = new Subject<BaseEvent>();

  // METRICS NOTIFICATIONS FIELDS
  private metricUpdatesIdsMap!: Map<number, number>;

  // ALARMS NOTIFICATIONS FIELDS
  private gatewayAlarmIdsMap!: Map<number, number>;
  private gatewayWorkflowIdsMap!: Map<number, number>;
  private deviceAlarmIdsMap!: Map<number, number>;
  private metricAlarmLiteIdsMap!: Map<number, number>;
  private metricAlarmDetailIdsMap!: Map<number, number>;

  // WRITE METRIC VALUE FIELDS
  private writeMetricValueIdsMap!: Map<number, number>;

  private readonly TOPIC_SEPARATOR: string = '/';
  private readonly BASE_EXPIRED_TOKEN_TOPIC: string = 'expired';
  private readonly APPLICATION_ONLINE_TOPIC: string = 'application/online';

  // METRICS
  private readonly BASE_METRIC_TOPIC: string = 'telemetry';
  private readonly SINGLE_SUBSCRIBER_COUNTER: number = 1;
  private readonly METRIC_NTF_SEPARATOR: string = '|';
  private readonly METRIC_NTF_TIMESTAMP: number = 0;
  private readonly METRIC_NTF_VALUE: number = 1;

  // ALARMS
  private readonly ALARM_TOPIC_BASE: string = 'alarm';
  private readonly WORKFLOW_TOPIC_BASE: string = 'wf_status';
  private readonly METRIC_LEVEL_LITE_ALARM_TOPIC: string = 'metric_lite';
  private readonly METRIC_LEVEL_DETAIL_ALARM_TOPIC: string = 'metric_detail';
  private readonly DEVICE_LEVEL_ALARM_TOPIC: string = 'device';
  private readonly GATEWAY_LEVEL_ALARM_TOPIC: string = 'gateway';
  private readonly GATEWAY_LEVEL_WORKFLOW_TOPIC: string = 'gateway';

  // WRITE METRIC
  private readonly WRITE_METRIC_TOPIC_BASE: string = 'wm2e';

  constructor() {
    super();
    this.initCollections();
  }

  public manageMqttConnection() {

    const username = this.gmService.user.pushNotificationInfo.brokerUsername;
    const password = this.gmService.user.pushNotificationInfo.brokerPassword;

    this.pushNotificationUserToken = this.gmService.user.pushNotificationInfo.notificationToken;

    const mqttOptions = {
      url: environment.brokerHost,
      username: username,
      password: password
    };

    this.mqttManager.setKeepAliveTime(30);

    this.mqttManager.connectToMqttBroker(mqttOptions);

    // EXPIRED
    this.registerToNotification(this.BASE_EXPIRED_TOKEN_TOPIC + this.TOPIC_SEPARATOR + this.pushNotificationUserToken, NotificationTokenExpiredNtf.NAME);

    // APPLICATION ONLINE STATUS
    this.registerToNotification(this.APPLICATION_ONLINE_TOPIC, ApplicationOnlineStatusChangedNtf.NAME);

  }

  addMetricIdsToNotifications(metricIds: Array<number>): void {

    // Questo metodo non sembra aver bisogno di mutex per sincronizzare l'accesso alla mappa metricUpdatesIdsMap
    // perché le operazioni eseguite sono tutte sincrone, quindi saranno eseguite in modo sincrono dall'event loop
    // e non c'è bisogno di sincronizzare l'accesso alla mappa
    // Sono stati fatti test con 100 componenti istanziati contemporaneamente che, alla creazione, aggiungono 3 metrica e alla distruzione rimuovono le metriche.
    // I test sono stati eseguiti con e senza mutex e non ci sono state differenze di comportamento, quindi il mutex non è necessario

    if (!!this.pushNotificationUserToken) {
      metricIds.forEach(metricId => {

        this.addIdToNotifications(metricId, this.metricUpdatesIdsMap, this.composeMetricNotificationTopic(this.pushNotificationUserToken!, metricId), UpdateMetricValueNtf.NAME);

      });
    } else {
      console.error("Can't add metrics to notifications: no valid token");
    }

  }

  removeMetricIdsToNotifications(metricIds: Array<number>): void {

    // Questo metodo non sembra aver bisogno di mutex per sincronizzare l'accesso alla mappa metricUpdatesIdsMap
    // perché le operazioni eseguite sono tutte sincrone, quindi saranno eseguite in modo sincrono dall'event loop
    // e non c'è bisogno di sincronizzare l'accesso alla mappa
    // Sono stati fatti test con 100 componenti istanziati contemporaneamente che, alla creazione, aggiungono 3 metrica e alla distruzione rimuovono le metriche.
    // I test sono stati eseguiti con e senza mutex e non ci sono state differenze di comportamento, quindi il mutex non è necessario

    if (!!this.pushNotificationUserToken) {
      metricIds.forEach(metricId => {

        this.removeIdToNotifications(metricId, this.metricUpdatesIdsMap, this.composeMetricNotificationTopic(this.pushNotificationUserToken!, metricId));

      });
    } else {
      console.error("Method blocked: no valid token");
    }


  }

  onUpdateMetricValue(callback: (event: UpdateMetricValueNtf) => void): Subscription {
    return this.on(UpdateMetricValueNtf.NAME, callback);
  }

  addWriteMetricIdsToNotifications(metricIds: number[]): void {

    if (this.pushNotificationUserToken) {
      metricIds.forEach(metricId => {
        this.addIdToNotifications(
          metricId,
          this.writeMetricValueIdsMap,
          this.composeWriteMetricNotificationTopic(this.pushNotificationUserToken!, metricId),
          WriteMetricValueNtf.NAME);
      });
    } else {
      console.error("Can't add metrics to notifications: no valid token");
    }
  }

  removeWriteMetricIdsToNotifications(metricIds: number[]): void {
    if (!!this.pushNotificationUserToken) {
      metricIds.forEach(metricId => {

        this.removeIdToNotifications(metricId, this.writeMetricValueIdsMap, this.composeGatewayAlarmNotificationTopic(this.pushNotificationUserToken!, metricId));

      });
    } else {
      console.error("Method blocked: no valid token");
    }
  }

  onUpdateWriteMetricValue(callback: (event: WriteMetricValueNtf) => void): Subscription {
    return this.on(WriteMetricValueNtf.NAME, callback);
  }

  addGatewayIdsToAlarmNotifications(gwIds: Array<number>): void {

    if (!!this.pushNotificationUserToken) {
      gwIds.forEach(gwId => {

        this.addIdToNotifications(gwId, this.gatewayAlarmIdsMap, this.composeGatewayAlarmNotificationTopic(this.pushNotificationUserToken!, gwId), GatewayStatusChangedNtf.NAME);

      });
    } else {
      console.error("Can't add metrics to notifications: no valid token");
    }

  }

  removeGatewayIdsToAlarmNotifications(gwIds: Array<number>): void {

    if (!!this.pushNotificationUserToken) {
      gwIds.forEach(gwId => {

        this.removeIdToNotifications(gwId, this.gatewayAlarmIdsMap, this.composeGatewayAlarmNotificationTopic(this.pushNotificationUserToken!, gwId));

      });
    } else {
      console.error("Method blocked: no valid token");
    }

  }

  onGatewayStatusChanged(callback: (event: GatewayStatusChangedNtf) => void): Subscription {
    return this.on(GatewayStatusChangedNtf.NAME, callback);
  }

  addGatewayIdsToWorkflowNotifications(gwIds: number[]): void {
    if (!!this.pushNotificationUserToken) {
      gwIds.forEach(gwId => {

        this.addIdToNotifications(gwId, this.gatewayWorkflowIdsMap, this.composeGatewayWorkflowNotificationTopic(this.pushNotificationUserToken!, gwId), GatewayWorkflowChangedNtf.NAME);

      });
    } else {
      console.error("Can't add metrics to notifications: no valid token");
    }
  }

  removeGatewayIdsToWorkflowNotifications(gwIds: number[]): void {
    if (!!this.pushNotificationUserToken) {
      gwIds.forEach(gwId => {

        this.removeIdToNotifications(gwId, this.gatewayWorkflowIdsMap, this.composeGatewayWorkflowNotificationTopic(this.pushNotificationUserToken!, gwId));

      });
    } else {
      console.error("Method blocked: no valid token");
    }
  }

  onGatewayWorkflowStatusChanged(callback: (event: GatewayWorkflowChangedNtf) => void): Subscription {
    return this.on(GatewayWorkflowChangedNtf.NAME, callback);
  }

  addDeviceIdsToAlarmNotifications(deviceIds: Array<number>): void {

    if (!!this.pushNotificationUserToken) {
      deviceIds.forEach(deviceId => {

        this.addIdToNotifications(deviceId, this.deviceAlarmIdsMap, this.composeDeviceAlarmNotificationTopic(this.pushNotificationUserToken!, deviceId), DeviceStatusChangedNtf.NAME);

      });
    } else {
      console.error("Can't add metrics to notifications: no valid token");
    }

  }

  removeDeviceIdsToAlarmNotifications(deviceIds: Array<number>): void {

    if (!!this.pushNotificationUserToken) {
      deviceIds.forEach(deviceId => {

        this.removeIdToNotifications(deviceId, this.deviceAlarmIdsMap, this.composeDeviceAlarmNotificationTopic(this.pushNotificationUserToken!, deviceId));

      });
    } else {
      console.error("Method blocked: no valid token");
    }

  }

  onDeviceStatusChanged(callback: (event: DeviceStatusChangedNtf) => void): Subscription {
    return this.on(DeviceStatusChangedNtf.NAME, callback);
  }

  addMetricIdsToAlarmLiteNotifications(metricIds: Array<number>): void {
    if (!!this.pushNotificationUserToken) {
      metricIds.forEach(metricId => {
        this.addIdToNotifications(metricId, this.metricAlarmLiteIdsMap, this.composeMetricAlarmLiteNotificationTopic(this.pushNotificationUserToken!, metricId), MetricAlarmStatusChangedLiteNtf.NAME);
      });
    } else {
      console.error("Can't add metrics to notifications: no valid token");
    }

  }

  // EXPIRED NOTIFICATION TOKEN

  addMetricIdsToAlarmDetailNotifications(metricId: number): void {
    if (!!this.pushNotificationUserToken) {
      this.addIdToNotifications(metricId, this.metricAlarmDetailIdsMap, this.composeMetricAlarmDetailNotificationTopic(this.pushNotificationUserToken!, metricId), MetricAlarmStatusChangedDetailNtf.NAME);
    } else {
      console.error("Can't add metrics to notifications: no valid token");
    }

  }

  removeMetricIdsToAlarmLiteNotifications(metricIds: Array<number>): void {

    if (!!this.pushNotificationUserToken) {
      metricIds.forEach(metricId => {

        this.removeIdToNotifications(metricId, this.metricAlarmLiteIdsMap, this.composeMetricAlarmLiteNotificationTopic(this.pushNotificationUserToken!, metricId));

      });
    } else {
      console.error("Method blocked: no valid token");
    }

  }

  removeMetricIdsToAlarmDetailNotifications(metricId: number): void {
    if (!!this.pushNotificationUserToken) {
      this.removeIdToNotifications(metricId, this.metricAlarmDetailIdsMap, this.composeMetricAlarmDetailNotificationTopic(this.pushNotificationUserToken!, metricId));
    } else {
      console.error("Method blocked: no valid token");
    }

  }

  // METRICS NOTIFICATIONS

  onMetricAlarmStatusChangedLite(callback: (event: MetricAlarmStatusChangedLiteNtf) => void): Subscription {
    return this.on(MetricAlarmStatusChangedLiteNtf.NAME, callback);
  }

  onMetricAlarmStatusChangedDetail(callback: (event: MetricAlarmStatusChangedDetailNtf) => void): Subscription {
    return this.on(MetricAlarmStatusChangedDetailNtf.NAME, callback);
  }

  onEvent(eventName: string, callback: (event: any) => void): Subscription {
    return this.on(eventName, callback);
  }

  emitEvent(event: BaseEvent) {
    this.subject$.next(event);
  }

  initCollections() {
    this.externalEventSubscriptionList$ = new Array<string>();
    this.externalEventSubscriptions$ = new Array<NotificationSubscription>();
    this.mapExtIntEvents = new Map<string, string>();
    this.mapEventTs = new Map<string, number>();
    this.metricUpdatesIdsMap = new Map<number, number>();
    this.gatewayAlarmIdsMap = new Map<number, number>();
    this.gatewayWorkflowIdsMap = new Map<number, number>();
    this.deviceAlarmIdsMap = new Map<number, number>();
    this.metricAlarmLiteIdsMap = new Map<number, number>();
    this.metricAlarmDetailIdsMap = new Map<number, number>();
    this.writeMetricValueIdsMap = new Map<number, number>();

  }

  cleanupServiceFields(): void {
    this.initCollections();
    this.clearExternalEventSubscriptions();
  }

  // GATEWAY ALARM NOTIFICATIONS

  private registerToNotification(externalEventName: string, eventName: string) {
    this.mapEvents(externalEventName, eventName);
    this.addExternalEventSubscription(externalEventName);
    this.mqttManager.addNewTopicSubscription(externalEventName);
  }

  private unregisterFromNotification(externalEventName: string) {
    this.removeExternalEventFromMap(externalEventName);
    this.removeExternalEventSubscription(externalEventName);
    this.mqttManager.removeNewTopicSubscription(externalEventName);
  }

  /**
   * Adds an external event to the list of events that comes from outside the application (via Library Event Bus) and subscribe to the event
   * @param eventName
   */
  private addExternalEventSubscription(eventName: string) {
    this.externalEventSubscriptionList$.push(eventName);

    const notifSub = new NotificationSubscription(eventName);
    notifSub.subscription = this.externalBusService.on(eventName, (value: any) => {
      // Calling private INJECT method
      this.injectEvent(new EventData(eventName, value));
    });

    this.externalEventSubscriptions$.push(notifSub);
  }

  private removeExternalEventSubscription(eventName: string) {
    const idx = this.externalEventSubscriptionList$.findIndex(event => event === eventName);

    if (idx !== -1) {
      this.externalEventSubscriptionList$.splice(idx, 1);

      const idxSub = this.externalEventSubscriptions$.findIndex(sub => sub.name === eventName);

      if (idxSub !== -1) {
        this.externalEventSubscriptions$[idxSub].subscription.unsubscribe();
        this.externalEventSubscriptions$.splice(idxSub, 1);
      }

    }
  }

  private clearExternalEventSubscriptions() {
    // this is used only when the token is expired and the user didn't receive a new one

    // Here the map function is used to create a copy of the array, so we can iterate over it and remove the subscriptions
    this.externalEventSubscriptionList$.map(a => a).forEach(event => {

      this.removeExternalEventFromMap(event);
      this.removeExternalEventSubscription(event);
      this.mqttManager.removeNewTopicSubscription(event);
    });
  }

  private restartAllExternalEventSubscriptionsWithNewToken() {
    // this is used when the token is expired and the user received a new one
    // the method is called after the clearExternalEventSubscriptions method
    // so we only need to perform a new subscription to all the events

    // EXPIRED

    this.registerToNotification(this.BASE_EXPIRED_TOKEN_TOPIC + this.TOPIC_SEPARATOR + this.pushNotificationUserToken, NotificationTokenExpiredNtf.NAME);

    // APPLICATION ONLINE STATUS

    this.registerToNotification(this.APPLICATION_ONLINE_TOPIC, ApplicationOnlineStatusChangedNtf.NAME);

    // METRICS TELEMETRY

    Array.from(this.metricUpdatesIdsMap.keys()).forEach(metricId => {
      this.registerToNotification(this.composeMetricNotificationTopic(this.pushNotificationUserToken!, metricId), UpdateMetricValueNtf.NAME);
    });

    // GATEWAYS

    Array.from(this.gatewayAlarmIdsMap.keys()).forEach(gatewayId => {
      this.registerToNotification(this.composeGatewayAlarmNotificationTopic(this.pushNotificationUserToken!, gatewayId), GatewayStatusChangedNtf.NAME);
    });

    Array.from(this.gatewayWorkflowIdsMap.keys()).forEach(gatewayId => {
      this.registerToNotification(this.composeGatewayWorkflowNotificationTopic(this.pushNotificationUserToken!, gatewayId), GatewayWorkflowChangedNtf.NAME);
    });

    // DEVICES

    Array.from(this.deviceAlarmIdsMap.keys()).forEach(deviceId => {
      this.registerToNotification(this.composeDeviceAlarmNotificationTopic(this.pushNotificationUserToken!, deviceId), DeviceStatusChangedNtf.NAME);
    });

    // METRICS ALARM

    Array.from(this.metricAlarmLiteIdsMap.keys()).forEach(metricId => {
      this.registerToNotification(this.composeMetricAlarmLiteNotificationTopic(this.pushNotificationUserToken!, metricId), MetricAlarmStatusChangedLiteNtf.NAME);
    });

    Array.from(this.metricAlarmDetailIdsMap.keys()).forEach(metricId => {
      this.registerToNotification(this.composeMetricAlarmDetailNotificationTopic(this.pushNotificationUserToken!, metricId), MetricAlarmStatusChangedDetailNtf.NAME);
    });

    // METRIC WRITE

    Array.from(this.writeMetricValueIdsMap.keys()).forEach(metricId => {
      this.registerToNotification(this.composeWriteMetricNotificationTopic(this.pushNotificationUserToken!, metricId), WriteMetricValueNtf.NAME);
    })

  }

  // DEVICE ALARM NOTIFICATIONS

  /**
   * Injects an EXTERNAL event to the Business Logic Bus
   * @param event the EXTERNAL event received
   */
  private injectEvent(event: EventData) {
    // Before emitting event in the local bus, it's necessary to convert the External EVENT DATA to a proper Internal Event class

    // EXTERNAL-TO-INTERNAL dispatching logics here (also EVENT CONVERSION)
    if (!this.mapExtIntEvents.has(event.name)) {
      console.log(`Received event I wasn\'t expecting (${event.name}), skipping...`);
    } else {
      // START EVENT CONVERSION

      // Retrieve internal event using events map
      const internalRetrievedEvent = this.mapExtIntEvents.get(event.name);


      // Switch case for the internal event
      switch (internalRetrievedEvent) {
        case NotificationTokenExpiredNtf.NAME:
          this.getNewNotificationToken();
          break;
        case ApplicationOnlineStatusChangedNtf.NAME: {
          const ev = this.getApplicationStatusChangedBodyFromEvent(event.value);
          if (ev.status) {

            // check version

            if (this.gmService.getAppVersion() !== ev.version) {
              // different version, reload page with F5

              window.location.reload();

            } else {
              // same version, do nothing

              this.blockForExpiredToken = false;
              this.getNewNotificationToken();
              this.subject$.next(new ApplicationOnlineStatusChangedNtf(ev));
            }

          } else {
            this.subject$.next(new ApplicationOnlineStatusChangedNtf(event.value));
            this.blockForExpiredToken = true;
            this.clearExternalEventSubscriptions();
            // Re-iscriviti su cambio di stato BE
            this.registerToNotification(this.APPLICATION_ONLINE_TOPIC, ApplicationOnlineStatusChangedNtf.NAME);
          }
          break;
        }
        case UpdateMetricValueNtf.NAME: {
          const ev = this.getMetricValueFromEvent(event.name, event.value);
          const tsKey = `${UpdateMetricValueNtf.NAME}:${ev.metricId}`;
          if (!this.mapEventTs.get(tsKey) || ev.timestamp > this.mapEventTs.get(tsKey)!) {
            this.subject$.next(new UpdateMetricValueNtf(ev));
            this.mapEventTs.set(tsKey, ev.timestamp);
          }
          break;
        }
        case GatewayStatusChangedNtf.NAME: {
          const ev = this.getGatewayAlarmStatusFromEvent(event.name, event.value);
          const tsKey = `${GatewayStatusChangedNtf.NAME}:${ev.gatewayId}`;
          if (!this.mapEventTs.get(tsKey) || ev.ts > this.mapEventTs.get(tsKey)!) {
            this.subject$.next(new GatewayStatusChangedNtf(ev));
            this.mapEventTs.set(tsKey, ev.ts);
          }
          break;
        }
        case GatewayWorkflowChangedNtf.NAME: {
          const ev = this.getGatewayWorkflowStatusFromEvent(event.name, event.value);
          const tsKey = `${GatewayWorkflowChangedNtf.NAME}:${ev.gatewayId}`;
          if (!this.mapEventTs.get(tsKey) || ev.ts > this.mapEventTs.get(tsKey)!) {
            this.subject$.next(new GatewayWorkflowChangedNtf(ev));
            this.mapEventTs.set(tsKey, ev.ts);
          }
          break;
        }
        case DeviceStatusChangedNtf.NAME: {
          const ev = this.getDeviceAlarmStatusFromEvent(event.name, event.value);
          const tsKey = `${DeviceStatusChangedNtf.NAME}:${ev.deviceId}`;
          if (!this.mapEventTs.get(tsKey) || ev.ts > this.mapEventTs.get(tsKey)!) {
            this.subject$.next(new DeviceStatusChangedNtf(ev));
            this.mapEventTs.set(tsKey, ev.ts);
          }
          break;
        }
        case MetricAlarmStatusChangedLiteNtf.NAME: {
          const ev = this.getMetricAlarmStatusLiteFromEvent(event.name, event.value);
          this.subject$.next(new MetricAlarmStatusChangedLiteNtf(ev));
          break;
        }
        case MetricAlarmStatusChangedDetailNtf.NAME: {
          const ev = this.getMetricAlarmStatusDetailFromEvent(event.name, event.value);
          this.subject$.next(new MetricAlarmStatusChangedDetailNtf(ev));
          break;
        }
        case WriteMetricValueNtf.NAME: {
          const ev = this.getWriteMetricValueFromEvent(event.name, event.value);
          const tsKey = `${WriteMetricValueNtf.NAME}:${ev.metricId}`;
          if (!this.mapEventTs.get(tsKey) || ev.ts > this.mapEventTs.get(tsKey)!) {
            this.subject$.next(new WriteMetricValueNtf(ev));
            this.mapEventTs.set(tsKey, ev.ts);
          }
          break;
        }
        default:
          console.warn(`Event I was expecting (${internalRetrievedEvent}) is not currently managed, skipping...`);
          return;
      }
    }
  }

  /**
   * Let a component subscribes to an event
   * @param event
   * @param action
   */
  private on(event: string, action: any): Subscription {
    // INTERNAL-TO-INTERNAL or EXTERNAL-TO-INTERNAL Subscription method
    return this.subject$.pipe(
      filter((e: BaseEvent) => e.eventName === event)
    ).subscribe(action);
  }

  /**
   * Maps an external event to an internal event, in order to bind two different event formats
   * @param externalEvent the external event
   * @param internalEvent the internal event
   */
  private mapEvents(externalEvent: string, internalEvent: string) {
    if (!this.mapExtIntEvents.has(externalEvent)) {
      this.mapExtIntEvents.set(externalEvent, internalEvent);
    } else if (this.mapExtIntEvents.get(externalEvent) !== internalEvent) {
      console.log('Changed external/internal mapping');
      this.mapExtIntEvents.set(externalEvent, internalEvent);
    }
  }

  private removeExternalEventFromMap(externalEvent: string) {
    this.mapExtIntEvents.delete(externalEvent);
  }

  private getNewNotificationToken(): void {

    const cb = new RefreshPushNotificationTokenCB();

    this.userService.refreshPushNotificationToken(cb).then((_) => {
      this.pushNotificationUserToken = cb.o.pushNotificationUserToken;

      this.clearExternalEventSubscriptions();
      this.restartAllExternalEventSubscriptionsWithNewToken();

    }).catch((error) => {

      console.error("Error while getting new notification token: " + error);

      this.blockForExpiredToken = true;
      // Error while getting new notification token
      // Previous token is expired, no new token provided, so no more notifications will be received
      // Unsubscribe from all notifications
      this.clearExternalEventSubscriptions();

    });

  }

  // METRICS ALARM NOTIFICATIONS

  private addIdToNotifications(id: number, map: Map<number, number>, externalEvent: string, internalEvent: string): void {
    const subscribers = map.get(id);

    if (subscribers === undefined) {
      // Add to list only if not previously present
      // and subscribe to notifications


      if (!this.blockForExpiredToken) {
        console.log("Adding metric " + id + " to notifications");

        map.set(id, this.SINGLE_SUBSCRIBER_COUNTER);
        this.registerToNotification(externalEvent, internalEvent);
      } else {
        console.error("Can't add metrics to notifications: token expired");
      }


    } else {
      // If already present, increment the counter
      console.log("Skip to add " + id + "... already present " + subscribers);
      map.set(id, subscribers + 1);
    }
  }

  private removeIdToNotifications(id: number, map: Map<number, number>, externalEvent: string) {
    const subscribers = map.get(id);

    if (!!subscribers) {

      if (subscribers > this.SINGLE_SUBSCRIBER_COUNTER) {
        map.set(id, subscribers - 1);
        console.log("Skip to remove " + id + ": others are listening " + (subscribers - 1));
        // Skipping unregistration, one or more components is still active
      } else {

        // Remove from list only if present
        // and unsubscribe from notifications
        console.log("It's the last subscriber. Removing " + id + " from notifications");
        map.delete(id);
        this.unregisterFromNotification(externalEvent);
      }

    }
  }

  private composeMetricNotificationTopic(token: string, metricInstanceId: number): string {
    return `${this.BASE_METRIC_TOPIC}${this.TOPIC_SEPARATOR}${token}${this.TOPIC_SEPARATOR}${metricInstanceId}`;
  }

  private getWriteMetricValueFromEvent(name: string, eventValue: any) {
    const value = (eventValue as string).split(this.METRIC_NTF_SEPARATOR);
    const metricId: number = Number(name.split(this.TOPIC_SEPARATOR).at(-1));
    const timestamp: number = Number(value[0]);
    const status: WriteOnEdgeStatusEnum = WriteOnEdgeStatusEnum[value[1] as keyof typeof WriteOnEdgeStatusEnum];
    const valueToWrite: number | boolean | string = value[2];
    const koMessage: string = value[3];
    return new WriteMetricValue(metricId, timestamp, valueToWrite, status, koMessage);
  }

  private composeWriteMetricNotificationTopic(token: string, metricInstanceId: number): string {
    return `${this.WRITE_METRIC_TOPIC_BASE}${this.TOPIC_SEPARATOR}${token}${this.TOPIC_SEPARATOR}${metricInstanceId}`;
  }

  private getApplicationStatusChangedBodyFromEvent(eventValue: any): ApplicationOnlineStatusChangedBody {
    const value = (eventValue as string).split(this.METRIC_NTF_SEPARATOR);

    return new ApplicationOnlineStatusChangedBody(value[0] === "true", value[1]);

  }

  private getMetricValueFromEvent(topic: string, eventValue: any): MetricValueUpdate {

    const metricInstanceId = topic.split(this.TOPIC_SEPARATOR)[2];
    // Value is a string in the form TIMESTAMP|VALUE
    const value = (eventValue as string).split(this.METRIC_NTF_SEPARATOR);

    if (!isNaN(parseFloat(value[this.METRIC_NTF_VALUE])))
      return new MetricValueUpdate(Number(metricInstanceId), Number(value[this.METRIC_NTF_TIMESTAMP]), Number(value[this.METRIC_NTF_VALUE]));
    else if (value[this.METRIC_NTF_VALUE] === "true" || value[this.METRIC_NTF_VALUE] === "false")
      return new MetricValueUpdate(Number(metricInstanceId), Number(value[this.METRIC_NTF_TIMESTAMP]), value[this.METRIC_NTF_VALUE] === "true");
    else
      return new MetricValueUpdate(Number(metricInstanceId), Number(value[this.METRIC_NTF_TIMESTAMP]), value[this.METRIC_NTF_VALUE]);

  }

  private getGatewayAlarmStatusFromEvent(topic: string, eventValue: any): GatewayAlarmStatus {
    const gatewayId = topic.split(this.TOPIC_SEPARATOR)[3];
    const value = eventValue.split(this.METRIC_NTF_SEPARATOR);
    const gatewayStatus = value[1];
    const ts = value[0];
    return new GatewayAlarmStatus(Number(gatewayId), Number(gatewayStatus), Number(ts));
  }

  private getGatewayWorkflowStatusFromEvent(topic: string, eventValue: any): GatewayWorkflowStatus {
    const gatewayId = topic.split(this.TOPIC_SEPARATOR)[3];
    const value = eventValue.split(this.METRIC_NTF_SEPARATOR);
    const gatewayStatus = value[1];
    const ts = value[0];
    return new GatewayWorkflowStatus(Number(gatewayId), gatewayStatus, Number(ts));
  }

  private composeBaseAlarmNotificationTopic(token: string): string {
    return `${this.ALARM_TOPIC_BASE}${this.TOPIC_SEPARATOR}${token}${this.TOPIC_SEPARATOR}`;
  }

  private composeBaseWorkflowNotificationTopic(token: string): string {
    return `${this.WORKFLOW_TOPIC_BASE}${this.TOPIC_SEPARATOR}${token}${this.TOPIC_SEPARATOR}`;
  }

  private composeGatewayAlarmNotificationTopic(token: string, gwId: number): string {
    return `${this.composeBaseAlarmNotificationTopic(token)}${this.GATEWAY_LEVEL_ALARM_TOPIC}${this.TOPIC_SEPARATOR}${gwId}`;
  }

  private composeGatewayWorkflowNotificationTopic(token: string, gwId: number): string {
    return `${this.composeBaseWorkflowNotificationTopic(token)}${this.GATEWAY_LEVEL_WORKFLOW_TOPIC}${this.TOPIC_SEPARATOR}${gwId}`;
  }

  private getDeviceAlarmStatusFromEvent(topic: string, eventValue: any): DeviceAlarmStatus {
    const deviceId = topic.split(this.TOPIC_SEPARATOR)[3];
    const value = eventValue.split(this.METRIC_NTF_SEPARATOR);
    const deviceStatus = value[1];
    const ts = value[0];
    const message = value[2];
    return new DeviceAlarmStatus(Number(deviceId), Number(deviceStatus), Number(ts), message);
  }

  private composeDeviceAlarmNotificationTopic(token: string, deviceId: number): string {
    return `${this.composeBaseAlarmNotificationTopic(token)}${this.DEVICE_LEVEL_ALARM_TOPIC}${this.TOPIC_SEPARATOR}${deviceId}`;
  }


  // OTHER EVENTS

  private getMetricAlarmStatusLiteFromEvent(topic: string, eventValue: any): MetricAlarmStatusLite {
    const value = (eventValue as string).split(this.METRIC_NTF_SEPARATOR);
    const alertId: number = Number(value[0]);
    const alertStatus: number = Number(value[1]);
    const telemetryValue: string = value[2];
    return new MetricAlarmStatusLite(alertId, alertStatus, telemetryValue);
  }

  private getMetricAlarmStatusDetailFromEvent(topic: string, eventValue: any): MetricAlarmStatusDetail {

    const value = (eventValue as string).split(this.METRIC_NTF_SEPARATOR);

    const alertId: number = Number(value[0]);
    const alertName: string = value[1];
    const isInAlarm: boolean = value[2] === "true";
    const alertStatus: number = Number(value[3]);
    const beginTs: number = Number(value[4]);
    const endTs: number = Number(value[5]);
    const telemetryValue: string = value[6];
    let message: string = "";
    if (value.length === 8)
      message = value[7];

    return new MetricAlarmStatusDetail(alertId, alertName, isInAlarm, alertStatus, beginTs, endTs, telemetryValue, message);
  }

  private composeMetricAlarmLiteNotificationTopic(token: string, metricId: number): string {
    return `${this.composeBaseAlarmNotificationTopic(token)}${this.METRIC_LEVEL_LITE_ALARM_TOPIC}${this.TOPIC_SEPARATOR}${metricId}`;
  }

  private composeMetricAlarmDetailNotificationTopic(token: string, metricId: number): string {
    return `${this.composeBaseAlarmNotificationTopic(token)}${this.METRIC_LEVEL_DETAIL_ALARM_TOPIC}${this.TOPIC_SEPARATOR}${metricId}`;
  }

}
